import { CommonErrors, type HomeDto, type ShiftType } from '@lmc/ts-common'
import {
  Add,
  Button,
  Form,
  FormCard,
  FormItem,
  Trash,
  theme
} from '@lmc/ui-kit'
import { Form as AntdForm, Input } from 'antd'
import { useWatch } from 'antd/lib/form/Form'
import { showToast } from 'common/utils'
import { Spinner } from 'components/generic'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { handleSubmit } from 'screens/Rostering/formComponents/utils'
import useCheckShiftType from 'screens/Rostering/hooks/useCheckShiftType'
import type { ShiftFormType } from 'screens/Rostering/hooks/useShiftTypesList'
import { getIsDifferentRule, getRequiredRule } from 'screens/Rostering/utils'
import styled from 'styled-components'
import FixedFooter from './FixedFooter'

type ShiftsFormProps = {
  types: ShiftType[]
  isShiftTypesLoading: boolean
  updateShiftTypes: (values: ShiftFormType) => void
  isDirty: boolean
  setIsDirty: (dirty: boolean) => void
}

const ShiftsForm = ({
  isShiftTypesLoading,
  updateShiftTypes,
  isDirty,
  setIsDirty
}: ShiftsFormProps) => {
  const { t } = useTranslation()

  const [form] = AntdForm.useForm()
  const [isUpdateLoading, setIsUpdateLoading] = useState(false)
  const { checkShiftTypeUsedInRota, isLoading: isCheckingShiftType } =
    useCheckShiftType()

  const home = useSelector(
    (state: { global: { home: HomeDto } }) => state.global.home
  )

  const initialValues = {
    types: home.rotaSettings?.shiftTypes
  }

  useWatch('types', form)

  const handleOnFinish = async (values: ShiftFormType) => {
    try {
      setIsUpdateLoading(true)
      setIsDirty(false)
      updateShiftTypes(values)
      setIsUpdateLoading(false)
    } catch (err) {
      const errorObj = err as { code?: number; message?: string } | null
      const errorCode = errorObj?.code

      if (errorCode === CommonErrors.Codes.rotaSettingsTypeInUse) {
        showToast({
          title: t('SHIFT_TYPE_IN_USE_ERROR'),
          type: 'error'
        })

        const newShiftValue: ShiftFormType['types'] = values.types.map(
          (value) => ({
            ...value,
            isDeleted: false
          })
        )

        form.setFieldValue('types', newShiftValue)
      } else {
        showToast({
          title: errorObj?.message || t('SETTINGS_ERROR'),
          type: 'error'
        })
      }
      setIsUpdateLoading(false)
    }
    setIsDirty(false)
  }

  const getDeleteFieldKey = (fieldName: number) => [
    'types',
    fieldName,
    'isDeleted'
  ]

  const deleteHandler = async (fieldName: number) => {
    const shiftTypeId = form.getFieldValue(['types', fieldName, 'id'])

    if (shiftTypeId) {
      try {
        const shiftTypeUsed = await checkShiftTypeUsedInRota({ shiftTypeId })
        const areAllArraysEmpty =
          shiftTypeUsed?.patterns?.length === 0 &&
          shiftTypeUsed?.instances?.length === 0 &&
          shiftTypeUsed?.revisions?.length === 0

        if (!areAllArraysEmpty) {
          showToast({
            title: t('SHIFT_TYPE_IN_USE_ERROR'),
            type: 'error'
          })
          return
        }
      } catch (err: any) {
        showToast({
          title: err?.message || t('SETTINGS_ERROR'),
          type: 'error'
        })
        return
      }
    }

    setIsDirty(true)
    const deleteFieldName = getDeleteFieldKey(fieldName)
    form.setFieldValue(deleteFieldName, true)
  }

  const filterDeletedFields = (fieldName: number) => {
    const deleteFieldName = getDeleteFieldKey(fieldName)
    const isDeleted = form.getFieldValue(deleteFieldName)

    return isDeleted !== true
  }

  const handleFormValuesChange = (values: ShiftFormType) => {
    if (values) {
      setIsDirty(true)
    }
  }

  const getIsShiftsDifferentRule = (fieldName: number) => {
    const formValues = form.getFieldsValue(true)
    const currentFormValues = formValues.types || []

    return getIsDifferentRule(currentFormValues, fieldName)
  }

  return (
    <FormContainer>
      {isShiftTypesLoading ? (
        <Spinner />
      ) : (
        <Form
          form={form}
          onFinish={handleOnFinish}
          onValuesChange={handleFormValuesChange}
          initialValues={initialValues}
          validateTrigger={['']}
        >
          <FormCard title={t('TYPES_SHIFTS')}>
            <AntdForm.List name="types">
              {(fields, { add }) => (
                <OptionsContainer>
                  {fields
                    .filter((field) => filterDeletedFields(field.name))
                    .map((field) => (
                      <OptionContainer>
                        <FormItem
                          name={[field.name, 'name']}
                          key={['types', field.key].join('-')}
                          rules={[
                            getRequiredRule(t('SHIFT_TYPE')),
                            getIsShiftsDifferentRule(field.name)
                          ]}
                        >
                          <Input />
                        </FormItem>
                        {fields?.filter((field) =>
                          filterDeletedFields(field.name)
                        ).length > 1 && (
                          <DeleteButton
                            variant="text"
                            disabled={isCheckingShiftType}
                            onClick={() => deleteHandler(field.name)}
                            ariaLabel={t('DELETE_ITEM')}
                          >
                            <Trash color={theme.color.neutral.color4} />
                          </DeleteButton>
                        )}
                      </OptionContainer>
                    ))}
                  <Button type="button" variant="text" onClick={() => add()}>
                    <Add />
                    {t('ADD_A_TYPE_OF_SHIFT')}
                  </Button>
                </OptionsContainer>
              )}
            </AntdForm.List>
          </FormCard>

          <FixedFooter
            isUpdateLoading={isUpdateLoading}
            isDirty={isDirty}
            handleSubmit={() => handleSubmit(form, handleOnFinish)}
          />
        </Form>
      )}
    </FormContainer>
  )
}

const FormContainer = styled.div`
  margin-bottom: 50px;
`

const DeleteButton = styled(Button)`
  height: 24px;
`

const OptionContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`

const OptionsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  width: 440px;
`

export default ShiftsForm
